import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { CategoryCombobox } from "@/components/category/combobox";
import { Badge } from "@/components/ui/badge";
import { useCategoryModal } from "@/context/category-modal-provider";
import { useSubscription } from "@/context/subscription-provider";
import useCategories from "@/hooks/use-categories";
import { cn } from "@/lib/utils";
import { Category, Transaction } from "@/types/transaction.type";
import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import { CalendarIcon, DollarSign, Loader2, PlusCircle } from "lucide-react";
import React from "react";
import { useForm } from "react-hook-form";
import { transactionFormSchema, TransactionFormValues } from "./form.schema";

interface TransactionFormProps {
  onSubmit: (data: Partial<Transaction>) => void;
  transaction?: Transaction;
  isSubmitting?: boolean;
  submitLabel?: string;
  cancelLabel?: string;
  onCancel?: () => void;
  showFooter?: boolean;
  defaultValues?: Partial<TransactionFormValues>;
  showQuickCategories?: boolean;
}

const TransactionForm: React.FC<TransactionFormProps> = ({
  onSubmit,
  transaction,
  isSubmitting = false,
  submitLabel = "Save",
  cancelLabel = "Cancel",
  onCancel,
  showFooter = true,
  defaultValues,
  showQuickCategories = false,
}) => {
  const {
    categories,
    categoriesByType,
    masterCategories,
    userCategories,
    categoryTotals,
  } = useCategories();
  const { showCreate } = useCategoryModal();
  const { hasSubscription } = useSubscription();
  // Get initial default values from props or transaction
  const initialValues = React.useMemo(() => {
    // Start with provided defaultValues if any
    const values: Partial<TransactionFormValues> = {
      ...defaultValues,
    };

    // If transaction is provided and a field isn't in defaultValues, use transaction data
    if (transaction) {
      if (values.id === undefined) values.id = transaction.id;
      if (values.amount === undefined) values.amount = transaction.amount;
      if (values.occurred === undefined) {
        if (transaction.occurred) {
          const parsedDate = new Date(transaction.occurred);
          values.occurred = isNaN(parsedDate.getTime())
            ? new Date()
            : parsedDate;
        } else {
          values.occurred = new Date();
        }
      }
      if (values.description === undefined)
        values.description = transaction.description || "";
      if (values.category === undefined)
        values.category = Number((transaction.category as Category)?.id);
    }

    // Set defaults for any remaining fields
    if (values.occurred === undefined) values.occurred = new Date();
    if (values.description === undefined) values.description = "";

    return values;
  }, [defaultValues, transaction]);
  // Initialize form with default values or existing transaction data

  const form = useForm<TransactionFormValues>({
    resolver: zodResolver(transactionFormSchema),
    defaultValues: initialValues,
  });

  const quickCategories = React.useMemo(() => {
    const categoryPool = [
      ...(masterCategories ?? []),
      ...(userCategories ?? []),
    ];
    if (!categoryPool.length) return [];

    const byId = new Map<string, Category>();
    categoryPool.forEach((category) => byId.set(String(category.id), category));

    const rankedByUsage =
      categoryTotals
        ?.slice()
        .sort((a, b) => b.count - a.count)
        .map((total) => byId.get(String(total.category)))
        .filter((category): category is Category => Boolean(category)) ?? [];

    if (rankedByUsage.length >= 3) {
      return rankedByUsage.slice(0, 4);
    }

    const fallback: Category[] = [];
    categoryPool.some((category) => {
      if (!fallback.some((pick) => String(pick.id) === String(category.id))) {
        fallback.push(category);
      }
      return fallback.length >= 4;
    });

    return fallback.slice(0, 4);
  }, [masterCategories, userCategories, categoryTotals]);

  // Update form when transaction data changes
  React.useEffect(() => {
    if (transaction) {
      form.reset({
        id: transaction.id,
        amount: transaction.amount,
        occurred: (() => {
          if (transaction.occurred) {
            const parsedDate = new Date(transaction.occurred);
            return isNaN(parsedDate.getTime()) ? new Date() : parsedDate;
          }
          return new Date();
        })(),
        description: transaction.description || "",
        category: Number((transaction.category as Category)?.id),
      });
    }
  }, [transaction, form]);

  const handleSubmit = async (values: TransactionFormValues) => {
    // Find the full category object based on the selected ID
    const selectedCategory = values.category
      ? categories?.find((c) => c.id === values.category)
      : undefined;

    // Create the complete transaction object with proper category format
    const transactionData = {
      ...values,
      // Use the full Category object as required by the Transaction type
      category: selectedCategory,
      // Format the date properly if needed
      occurred:
        values.occurred instanceof Date
          ? format(values.occurred, "yyyy-MM-dd")
          : values.occurred,
    };

    onSubmit(transactionData);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Amount Field */}
          <FormField
            control={form.control}
            name="amount"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Amount</FormLabel>
                <FormControl>
                  <div className="relative">
                    <DollarSign className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="0.00"
                      className="pl-8"
                      type="number"
                      step="0.01"
                      {...field}
                    />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Date Field */}
          <FormField
            control={form.control}
            name="occurred"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Date</FormLabel>
                <Popover modal>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full pl-3 text-left font-normal",
                          !field.value && "text-muted-foreground",
                        )}
                      >
                        {field.value ? (
                          format(field.value, "PPP")
                        ) : (
                          <span>Pick a date</span>
                        )}
                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={field.value}
                      onSelect={field.onChange}
                      initialFocus
                      captionMode="default"
                      fromYear={1900}
                      toYear={2100}
                    />
                  </PopoverContent>
                </Popover>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Category Field - Now updates type automatically */}
        <FormField
          control={form.control}
          name="category"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Category</FormLabel>
              <div className="w-full flex items-center">
                <CategoryCombobox
                  groups={categoriesByType.map((group) => ({
                    label: group.type.name,
                    options: group.categories.map((c) => ({
                      label: c.name,
                      value: c.id.toString(),
                      color: c.color as string,
                    })),
                  }))}
                  value={field.value?.toString()}
                  onValueChange={(value) => {
                    // Update category_id
                    field.onChange(value ? Number(value) : undefined);
                  }}
                  placeholder="Select a category"
                  searchPlaceholder="Search categories..."
                  className="flex-1"
                  modal={true}
                />
                {hasSubscription && (
                  <Button
                    variant="ghost"
                    className="ml-2 px-2"
                    onClick={(e) => {
                      e.preventDefault();
                      showCreate((newCategory) => {
                        // Auto-select the newly created category
                        field.onChange(newCategory.id);
                      });
                    }}
                  >
                    <PlusCircle className="h-4 w-4" />
                  </Button>
                )}
              </div>
              {showQuickCategories && quickCategories.length > 0 && (
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {quickCategories.map((category) => (
                    <Badge
                      key={category.id}
                      variant="outline"
                      className="cursor-pointer border text-xs px-2 py-1 rounded-full"
                      style={{
                        borderColor: category.color as string,
                        background: `${category.color as string}1a`,
                      }}
                      onClick={() => field.onChange(category.id)}
                    >
                      {category.name}
                    </Badge>
                  ))}
                </div>
              )}
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Description Field */}
        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Enter transaction description"
                  className="resize-none"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Form Footer */}
        {showFooter && (
          <div className="flex justify-end space-x-2 pt-4">
            {onCancel && (
              <Button
                className="w-full"
                type="button"
                variant="outline"
                onClick={onCancel}
              >
                {cancelLabel}
              </Button>
            )}
            <Button className="w-full" type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 animate-spin" />}
              {submitLabel}
            </Button>
          </div>
        )}
      </form>
    </Form>
  );
};

export default TransactionForm;
